<template>
  <modal
    :show="isVisible"
    :title="modalTitle"
    elem-id="modal_crud_so-product-categories"
    size="small"
    :close-label="t('Cancelar')"
    :save-label="isDeleteMode ? (isDeleting ? t('Deletando') : t('Deletar')) : (isSaving ? t('Salvando') : t('Salvar'))"
    :save-fn="isDeleteMode ? handleDelete : handleSave"
    @close="handleClose"
    @dismiss="handleClose"
  >
    <!-- Content only renders when modal is visible (prevents API calls on mount) -->
    <template v-if="isVisible">
      <!-- Delete Confirmation Mode -->
      <div v-if="isDeleteMode" class="delete-confirmation">
        <p>{{ t('Tem certeza que deseja deletar esta categoria de produto?') }}</p>
        <p class="text-muted">{{ itemToDelete?.so_product_category_name }}</p>
      </div>

      <!-- Create/Edit Mode -->
      <div v-else>
        <s-o-product-categories-form
          ref="formRef"
          :itemId="currentId ?? undefined"
          :isEditMode="isEditMode"
          @submit-success="handleFormSuccess"
          @submit-error="handleFormError"
        />
        <div v-if="isEditMode" class="delete-action">
          <Button
            data-testid="so-product-categories-delete-button"
            severity="danger"
            outlined
            icon="pi pi-trash"
            :label="t('Deletar')"
            @click="enterDeleteConfirmation"
          />
        </div>
      </div>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useSOCatalog } from '../../composables/useSOCatalog';
import { eventBus } from '../../eventBus';
import SOProductCategoriesForm from './SOProductCategoriesForm.vue';
import { log, warn as logWarn, error as logError } from '../../utils/DebugLogger';

type Id = number | string;

interface FormInstance {
  resetForm: () => void;
  loadForEdit: () => Promise<void>;
  handleSubmit: () => Promise<void>;
}

interface ItemToDelete {
  so_product_category_name?: string;
  [key: string]: unknown;
}

interface ModalOpenPayload {
  id?: Id;
  data?: ItemToDelete | null;
  action?: 'create' | 'edit' | 'delete';
}

// Get i18n composable
const { t } = useI18n();

// Use composables
const { remove } = useSOCatalog('product_categories');

// Reactive data
const isVisible = ref<boolean>(false);
const isEditMode = ref<boolean>(false);
const isDeleteMode = ref<boolean>(false);
const currentId = ref<Id | null>(null);
const itemToDelete = ref<ItemToDelete | null>(null);
const formRef = ref<FormInstance | null>(null);
const isSaving = ref<boolean>(false);
const isDeleting = ref<boolean>(false);

// Computed properties
const modalTitle = computed<string>(() => {
  if (isDeleteMode.value) {
    return t('Deletar Categoria de Produto');
  } else if (isEditMode.value) {
    return t('Editar Categoria de Produto');
  } else {
    return t('Adicionar Categoria de Produto');
  }
});

// Methods
const openForCreate = (): void => {
  log('SOProductCategoriesModal', 'openForCreate', 'Opening modal for create');
  isVisible.value = true;
  isEditMode.value = false;
  isDeleteMode.value = false;
  currentId.value = null;

  // Reset form after modal is visible
  nextTick(() => {
    if (formRef.value) {
      formRef.value.resetForm();
    }
  });
};

const openForEdit = async (id: Id): Promise<void> => {
  log('SOProductCategoriesModal', 'openForEdit', 'Opening modal for edit', id);
  isVisible.value = true;
  isEditMode.value = true;
  isDeleteMode.value = false;
  currentId.value = id;

  // Wait for the form component to be rendered
  await nextTick();

  // Explicitly load data for edit
  if (formRef.value) {
    await formRef.value.loadForEdit();
  }
};

const openForDelete = async (id: Id, data: ItemToDelete | null = null): Promise<void> => {
  log('SOProductCategoriesModal', 'openForDelete', 'Opening modal for delete', id);
  isVisible.value = true;
  isEditMode.value = false;
  isDeleteMode.value = true;
  currentId.value = id;
  itemToDelete.value = data;
};

const enterDeleteConfirmation = (): void => {
  log('SOProductCategoriesModal', 'enterDeleteConfirmation', 'Switching to delete confirmation', currentId.value);
  isEditMode.value = false;
  isDeleteMode.value = true;
};

const handleClose = (): void => {
  log('SOProductCategoriesModal', 'handleClose', 'Closing modal');
  isVisible.value = false;
  isEditMode.value = false;
  isDeleteMode.value = false;
  currentId.value = null;
  itemToDelete.value = null;
  isSaving.value = false;
  isDeleting.value = false;

  // Reset form
  if (formRef.value) {
    formRef.value.resetForm();
  }
};

const handleSave = async (): Promise<void> => {
  log('SOProductCategoriesModal', 'handleSave', 'Save button clicked');
  if (formRef.value) {
    isSaving.value = true;
    try {
      await formRef.value.handleSubmit();
    } finally {
      isSaving.value = false;
    }
  }
};

const handleDelete = async (): Promise<void> => {
  if (!currentId.value) {
    logWarn('SOProductCategoriesModal', 'handleDelete', 'No ID to delete');
    return;
  }

  log('SOProductCategoriesModal', 'handleDelete', 'Deleting product category', currentId.value);
  isDeleting.value = true;

  try {
    await remove({ so_product_category_id: currentId.value });
    eventBus.emit('globalMessage.success', t('Categoria de produto deletada com sucesso'));
    eventBus.emit('so-product-categories.deleted');
    handleClose();
  } catch (error) {
    const err = error as Error;
    logError('SOProductCategoriesModal', 'handleDelete', 'Error deleting product category', err);
    eventBus.emit('globalMessage.error', t('Erro ao deletar categoria de produto') + ': ' + err.message);
  } finally {
    isDeleting.value = false;
  }
};

const handleFormSuccess = (): void => {
  log('SOProductCategoriesModal', 'handleFormSuccess', 'Form submitted successfully');
  eventBus.emit('so-product-categories.saved');
  handleClose();
};

const handleFormError = (error: unknown): void => {
  logError('SOProductCategoriesModal', 'handleFormError', 'Form submission error', error);
  // Error message is already shown by the form component
};

// Standard modal open handler (for modal.so-product-categories.open event)
const handleModalOpen = async (data?: ModalOpenPayload): Promise<void> => {
  log('SOProductCategoriesModal', 'handleModalOpen', 'Standard modal open event received', data);

  if (data?.action === 'delete' && data?.id) {
    await openForDelete(data.id, data.data || null);
  } else if (data?.id) {
    await openForEdit(data.id);
  } else {
    openForCreate();
  }
};

// Lifecycle - Event listeners
onMounted(() => {
  log('SOProductCategoriesModal', 'onMounted', 'Component mounted, setting up event listeners');

  // Standard modal events (preferred pattern)
  eventBus.on('modal.so-product-categories.open', handleModalOpen);
  eventBus.on('modal.so-product-categories.close', handleClose);
});

onBeforeUnmount(() => {
  log('SOProductCategoriesModal', 'onBeforeUnmount', 'Component unmounting, cleaning up event listeners');

  // Remove standard modal events
  eventBus.off('modal.so-product-categories.open', handleModalOpen);
  eventBus.off('modal.so-product-categories.close', handleClose);
});

// Expose methods for parent components (legacy support)
defineExpose({
  openForCreate,
  openForEdit,
  openForDelete
});
</script>

<style scoped>
.delete-confirmation {
  padding: 20px;
  text-align: center;
}

.delete-confirmation p {
  margin-bottom: 10px;
  font-size: 16px;
}

.text-muted {
  color: #6c757d;
  font-style: italic;
}

.delete-action {
  display: flex;
  justify-content: flex-end;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid var(--app-bg-dark, #ccc);
}
</style>
